import { auth, functions, db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, updateDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { waitForProcessingComplete } from './processingStatusHelpers';

/**
 * Map a report stage display name to its processing-status field under
 * dataModel.6_manageReports. Shared by the client-status and defer-to-server
 * paths so both stay in sync.
 */
function resolveStatusField(reportStage) {
  switch (reportStage) {
    case 'Clinical Interview':
      return 'ciReportProcessingStatus';
    case 'Semi-Structured Interview':
      return 'ssiReportProcessingStatus';
    case 'Diagnostic Decision':
      return 'ddReportProcessingStatus';
    case 'Post-Assessment Discussion':
      return 'padReportProcessingStatus';
    case 'QbTest':
      return 'qbTestReportProcessingStatus';
    case 'QbCheck':
      return 'qbCheckReportProcessingStatus';
    case 'Pre-Assessment Report':
      return 'preARReportProcessingStatus';
    default:
      return null;
  }
}

/**
 * Fire the renderAndStoreDocument callable without awaiting completion.
 * The CF writes 'complete' / 'error' server-side. On non-timeout client
 * errors we optionally flip the status to 'error' when the caller owns
 * the client-side status write (deferStatusToServer = false).
 */
function invokeRenderCallable({
  reportStage,
  clinicianId,
  patientId,
  statusField,
  patientDocRef,
  deferStatusToServer,
}) {
  const renderAndStoreDocument = httpsCallable(functions, 'renderAndStoreDocument', {
    timeout: 360000, // 6 minutes (slightly longer than server timeout of 5 minutes)
  });

  renderAndStoreDocument({
    report_stage: reportStage,
    clinician_id: clinicianId,
    patient_id: patientId,
  }).then((result) => {
    console.log('Document generated successfully:', result.data);
  }).catch((error) => {
    console.error('Error in renderAndStoreDocument function:', error);

    // deadline-exceeded means the client timed out but the CF is still
    // running server-side — don't flip to 'error'.  The self-healing
    // timer in DocumentsDashboard will catch it if the CF truly failed.
    if (error.code === 'deadline-exceeded') {
      console.log('Client-side timeout occurred, but function continues processing in background');
      return;
    }

    // When the server owns the status write (Support-safe path), leave
    // error handling to the CF / stuck-processing self-heal — the client
    // often cannot write dataModel for Support / unassigned Org Admin.
    if (deferStatusToServer) {
      return;
    }

    console.error('Actual error occurred, updating status to error');
    updateDoc(patientDocRef, {
      [`dataModel.6_manageReports.${statusField}`]: 'error',
    }).catch((updateError) => {
      console.error('Error updating processing status:', updateError);
    });
  });
}

/**
 * Trigger document template rendering for a given report stage.
 *
 * Default behaviour (RD_* Save & Exit):
 *   Uses a Firestore transaction to atomically guard against duplicate
 *   invocations: if the status is already 'processing', we bail out instead
 *   of overwriting a potentially-arrived 'complete' from an earlier CF run.
 *   A `*ProcessingStartedAt` timestamp is written alongside the status so
 *   DocumentsDashboard can detect stuck 'processing' states (>10–15 min).
 *
 * Options (appointment-date re-finalise and similar Support-safe callers):
 *   deferStatusToServer — skip the client dataModel write and let
 *     renderAndStoreDocument set processing status via Admin SDK. Required
 *     for Support / unassigned Org Admin (they cannot write dataModel).
 *     Matches the QBCheckFormTOC direct-CF pattern.
 *   waitAndRetry — if the status is already 'processing', wait for it to
 *     finish then invoke exactly one more render (max 2 runs). Mirrors
 *     rerenderPatientReportIfStale concurrency handling.
 */
export const TriggerRenderDocTemplate = async ({
  reportStage,
  clinicianId,
  patientId,
  deferStatusToServer = false,
  waitAndRetry = false,
}) => {
  const user = auth.currentUser;
  if (!user || !user.uid) return { success: false, error: 'User not authenticated' };

  if (!reportStage || !clinicianId || !patientId) {
    console.error('Missing required parameters:', { reportStage, clinicianId, patientId });
    return { success: false, error: 'Missing required parameters' };
  }

  const statusField = resolveStatusField(reportStage);
  if (!statusField) {
    console.error('Unknown report stage:', reportStage);
    return { success: false, error: 'Unknown report stage' };
  }

  const patientDocRef = doc(db, 'patients', patientId);
  const startedAtField = statusField.replace('ProcessingStatus', 'ProcessingStartedAt');

  const fireRender = () => {
    invokeRenderCallable({
      reportStage,
      clinicianId,
      patientId,
      statusField,
      patientDocRef,
      deferStatusToServer,
    });
  };

  try {
    // ── Support-safe / deferred-status path ──────────────────────────────
    // Skip the client transaction. Optionally wait-then-retry if another
    // render is already in flight for this stage.
    if (deferStatusToServer) {
      const snap = await getDoc(patientDocRef);
      const current = snap.data()?.dataModel?.['6_manageReports']?.[statusField];

      if (current === 'processing') {
        if (!waitAndRetry) {
          console.log(`Skipping ${reportStage} render — already processing`);
          return { success: true, data: { status: 'already-processing' } };
        }

        console.log(
          `${reportStage} already processing — waiting for completion before retry`
        );
        await waitForProcessingComplete(patientId, statusField, 6 * 60 * 1000);

        const recheckSnap = await getDoc(patientDocRef);
        const recheckStatus =
          recheckSnap.data()?.dataModel?.['6_manageReports']?.[statusField];
        if (recheckStatus === 'processing') {
          return { success: true, data: { status: 'still-processing-after-wait' } };
        }

        console.log(`${reportStage} retry after wait — invoking render`);
        fireRender();
        return { success: true, data: { status: 'processing' } };
      }

      fireRender();
      return { success: true, data: { status: 'processing' } };
    }

    // ── Default path: client owns the processing-status write ────────────
    let alreadyProcessing = false;
    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(patientDocRef);
      const current = snap.data()?.dataModel?.['6_manageReports']?.[statusField];
      if (current === 'processing') {
        alreadyProcessing = true;
        return;
      }
      transaction.update(patientDocRef, {
        [`dataModel.6_manageReports.${statusField}`]: 'processing',
        [`dataModel.6_manageReports.${startedAtField}`]: serverTimestamp(),
      });
    }, { maxAttempts: 10 });

    if (alreadyProcessing) {
      if (!waitAndRetry) {
        console.log(`Skipping ${reportStage} render — already processing`);
        return { success: true, data: { status: 'already-processing' } };
      }

      console.log(
        `${reportStage} already processing — waiting for completion before retry`
      );
      await waitForProcessingComplete(patientId, statusField, 6 * 60 * 1000);

      // One retry: try to claim processing again, then fire.
      let stillBusy = false;
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(patientDocRef);
        const current = snap.data()?.dataModel?.['6_manageReports']?.[statusField];
        if (current === 'processing') {
          stillBusy = true;
          return;
        }
        transaction.update(patientDocRef, {
          [`dataModel.6_manageReports.${statusField}`]: 'processing',
          [`dataModel.6_manageReports.${startedAtField}`]: serverTimestamp(),
        });
      }, { maxAttempts: 10 });

      if (stillBusy) {
        return { success: true, data: { status: 'still-processing-after-wait' } };
      }

      fireRender();
      return { success: true, data: { status: 'processing' } };
    }

    fireRender();
    return { success: true, data: { status: 'processing' } };
  } catch (error) {
    console.error('Error setting processing status:', error);
    return { success: false, error };
  }
};
